import { createElement, forwardRef, type ReactNode } from "react";

/**
 * Ultra-light drop-in replacement for the small slice of the framer-motion API
 * this site uses (`motion.<tag>` with initial/animate/whileInView and
 * `AnimatePresence`).
 *
 * It removes framer-motion (~34KB gzip) and — more importantly — its per-frame
 * mount/scroll animation work, which was the dominant source of main-thread
 * "Style & Layout" time that delayed the LCP hero paint on mobile.
 *
 * Elements render immediately at their natural (visible) state: the decorative
 * entrance/scroll fade is dropped, but layout, content, and styling are
 * unchanged. This is intentionally dependency-free (no IntersectionObserver,
 * timers, or state) so content can never be left hidden in any environment.
 */

const FRAMER_PROPS = new Set([
  "initial",
  "animate",
  "whileInView",
  "whileHover",
  "whileTap",
  "whileFocus",
  "whileDrag",
  "exit",
  "transition",
  "viewport",
  "variants",
  "custom",
  "layout",
  "layoutId",
  "onViewportEnter",
  "onViewportLeave",
  "drag",
  "dragConstraints",
]);

function createMotionComponent(tag: string) {
  const Comp = forwardRef<HTMLElement, Record<string, unknown>>(function MotionLite(props, ref) {
    const domProps: Record<string, unknown> = {};
    for (const key in props) {
      if (!FRAMER_PROPS.has(key) && key !== "ref") domProps[key] = props[key];
    }
    return createElement(tag, { ...domProps, ref });
  });
  Comp.displayName = `motion.${tag}`;
  return Comp;
}

const cache = new Map<string, ReturnType<typeof createMotionComponent>>();

export const motion: Record<string, ReturnType<typeof createMotionComponent>> = new Proxy(
  {},
  {
    get(_target, tag: string) {
      if (!cache.has(tag)) cache.set(tag, createMotionComponent(tag));
      return cache.get(tag);
    },
  }
) as Record<string, ReturnType<typeof createMotionComponent>>;

/** Minimal AnimatePresence: renders children (no exit animation). */
export function AnimatePresence({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
